/**
 * 设计令牌：全站唯一的色值、字号、圆角、密度来源（ARCHITECTURE §20.4）。
 *
 * 取向是企业后台的"高密度 + 低饱和"：中性灰蓝打底，一种强调色，四种语义色，
 * 面板只用 1px 边框分层、不用阴影。业务代码一律通过主题 token 取色，不直接引用本文件，
 * 只有图表（接受字符串颜色、拿不到 CSS 变量）例外，见 ./charts。
 */

/** 中性色：界面的骨架。0 最亮、950 最暗，浅色与深色模式共用同一条色阶。 */
export const neutral = {
  0: "#ffffff",
  25: "#fbfcfd",
  50: "#f6f8fa",
  100: "#eef1f4",
  150: "#e7eaee",   // 描边专用：比 200 更淡，卡片边界只做分隔不抢眼
  200: "#dde2e7",
  300: "#c8cfd6",
  400: "#9aa4b0",
  500: "#717d8a",
  600: "#55606d",
  700: "#3d4752",
  800: "#272f38",
  850: "#1d242b",
  900: "#151b21",
  950: "#0d1216",
} as const;

/** 强调色：主按钮、选中态、链接、单系列图表。 */
export const accent = {
  50: "#eef4ff",
  100: "#dbe7fe",
  200: "#bcd2fd",
  300: "#8eb4fb",
  400: "#5a8df5",
  500: "#2f6ae8",
  600: "#1a51cc",
  700: "#153fa3",
  800: "#143681",
  900: "#132f69",
} as const;

/** 语义色：只表达好坏，永远与文字同时出现（labels.ts 决定用哪一种）。 */
export const success = {
  50: "#e9f8ef",
  100: "#cdeeda",
  200: "#9cdcb8",
  300: "#5fc38e",
  400: "#2ea66b",
  500: "#15854f",
  600: "#0f6a3f",
  700: "#0d5433",
  800: "#0b432a",
  900: "#093723",
} as const;

export const warning = {
  50: "#fff6e6",
  100: "#ffe7bf",
  200: "#ffd089",
  300: "#f7b14a",
  400: "#e19217",
  500: "#b87412",
  600: "#945c0f",
  700: "#76490d",
  800: "#5f3b0c",
  900: "#4e310b",
} as const;

export const danger = {
  50: "#fdeded",
  100: "#fbd8d8",
  200: "#f6b2b2",
  300: "#ee8080",
  400: "#e05252",
  500: "#c62f2f",
  600: "#a32222",
  700: "#841d1d",
  800: "#6c1919",
  900: "#5a1717",
} as const;

export type Ramp = typeof neutral | typeof accent | typeof success | typeof warning | typeof danger;

/** 字体。界面字号整体偏小，和成熟后台一致：正文 13px，标题靠字重而非字号拉开层次。 */
export const font = {
  sans: `"Inter Variable", "Inter", -apple-system, "PingFang SC", "Microsoft YaHei", system-ui, sans-serif`,
  mono: `"JetBrains Mono", "SFMono-Regular", Menlo, Consolas, monospace`,
} as const;

/** 密度：控件高度与行高。改这里就能整体调松紧，页面里不要再写死像素。 */
export const density = {
  control: 32,
  controlSmall: 28,
  rowHeight: 40,
  headerHeight: 36,
  sidebarWidth: 232,
  sidebarItem: 32,
  topbarHeight: 44,
  cardPadding: 16,
  pageMaxWidth: 1760,
  /** 表单类页面的内容宽度：比列表窄（字段排太宽反而难扫），但要跟着屏幕长。 */
  formMaxWidth: { xs: "100%", lg: 1200, xl: 1440 },
} as const;

export const radius = { sm: 3, md: 4, lg: 6, pill: 999 } as const;
